import {
  DEFAULT_STACK_TOWER_CONFIG,
  createInitialTowerState,
  currentMovingBlockFootprint,
  deriveRunResult,
  footprintWidth,
  multiplierForClimb,
  placeBlock,
  resetTowerKeepingScore,
  zoneForHeight,
  type BlockFootprint,
  type HeadToHeadPlayerResult,
  type OverhangPiece,
  type StackTowerConfig,
  type StackTowerZone,
  type TowerRunState,
} from '@ranked-waiting/stack-tower-core';
import { mountCanvasSurface } from './stack-tower-canvas';
import { bindStackTowerInput } from './stack-tower-input';
import { cameraOffsetForTowerTop, dampTowardTarget, worldToScreenY } from './stack-tower-camera';
import { drawStackTowerFrame, type SettledBlockView } from './stack-tower-render';
import { spawnMissPiece, spawnOverhangPiece, stepFallingPiece, type FallingPiece } from './stack-tower-physics';
import { playStackTowerHit, playStackTowerMiss, playStackTowerNearMiss, playStackTowerPerfect } from './stack-tower-audio';

/**
 * C015 Phase 4 — the two live tower panes of a split-screen head-to-head.
 * Deliberately a sibling of `stack-tower-engine.ts` (Solo Endless), not a
 * generalisation of it: the timed match's rules are genuinely different (a
 * miss resets the tower instead of ending the run, the match ends on a
 * server-authoritative clock instead of the first miss, and one side is
 * driven by relayed network events instead of local input) and Solo Endless
 * is shipped and tested — this file never touches it. What *is* shared
 * between this file's own two panes (the interactive local tower and the
 * reactive opponent reconstruction) is the per-frame bookkeeping — camera
 * damping, falling-debris stepping, impact/shake decay, the draw call — so
 * that lives once in `stepAndDrawFrame` below. Same core sim as Solo Endless
 * throughout (`placeBlock`, `currentMovingBlockFootprint`), configured with
 * `resetTowerKeepingScore` for the timed miss rule — never forked.
 */

const MAX_DELTA_MS = 100;
const MOVING_BLOCK_COLOR = '#f3f1e9'; // --colour-paper
const SHAKE_DURATION_MS = 220;
const HIT_SHAKE_UNITS = 4;
const PERFECT_SHAKE_UNITS = 6;
const MISS_SHAKE_UNITS = 13;
const NEAR_MISS_SHAKE_UNITS = 9;
/** A surviving sliver narrower than this fraction of the starting width reads as "barely made it" (spec §12), not just an off-centre hit. */
const NEAR_MISS_WIDTH_FRACTION = 0.12;
const PIECE_CULL_MARGIN_PX = 140;

/**
 * What gets relayed over the socket for the opponent to reconstruct with
 * (see `stack-tower-match-session.ts`). The server never interprets this —
 * it only relays it verbatim plus peeks at `.score` as a finalisation
 * fallback. `height` (blocks climbed since the last reset) is carried
 * separately from `score` — score can now outpace height once the
 * multiplier kicks in (rules.ts), so the opponent pane's camera/zone need
 * their own explicit signal rather than inferring it from score.
 */
export type StackTowerPlaceEvent =
  | {
      kind: 'hit';
      score: number;
      height: number;
      worldY: number;
      footprint: BlockFootprint;
      perfect: boolean;
      overhang: OverhangPiece | null;
      /** Perfect streak reached by this placement (0 if not perfect) — drives the opponent pane's escalating flash tier too, same as the local pane. */
      streak: number;
      /** A valid hit that barely survived (spec §12) — feedback intensity only, never changes hit/miss. */
      nearMiss: boolean;
    }
  | { kind: 'miss'; score: number; height: number; worldY: number; movingBlock: BlockFootprint };

interface TowerVisual {
  settledBlocks: SettledBlockView[];
  fallingPieces: FallingPiece[];
  cameraOffset: number;
  shakeMag: number;
  shakeElapsedMs: number;
  impactWorldY: number;
  impactStartedMs: number;
  impactPerfect: boolean;
  impactStreak: number;
  impactNearMiss: boolean;
}

function createVisual(initialFootprint: BlockFootprint): TowerVisual {
  return {
    settledBlocks: [{ footprint: initialFootprint, worldY: 0 }],
    fallingPieces: [],
    cameraOffset: 0,
    shakeMag: 0,
    shakeElapsedMs: 0,
    impactWorldY: 0,
    impactStartedMs: -Infinity,
    impactPerfect: false,
    impactStreak: 0,
    impactNearMiss: false,
  };
}

function resetVisualTower(v: TowerVisual, initialFootprint: BlockFootprint): void {
  v.settledBlocks = [{ footprint: initialFootprint, worldY: 0 }];
}

function triggerShake(v: TowerVisual, units: number, reducedMotion: boolean): void {
  if (reducedMotion) return;
  v.shakeMag = units;
  v.shakeElapsedMs = 0;
}

function triggerImpact(v: TowerVisual, worldY: number, perfect: boolean, streak: number, nearMiss: boolean, nowMs: number, reducedMotion: boolean): void {
  if (reducedMotion) return;
  v.impactWorldY = worldY;
  v.impactStartedMs = nowMs;
  v.impactPerfect = perfect;
  v.impactStreak = streak;
  v.impactNearMiss = nearMiss;
}

/**
 * Advances the shared per-frame bookkeeping (camera damp, falling-debris
 * stepping/culling, shake decay) and draws one frame. Used by both panes so
 * they stay visually identical — the only difference between them is what
 * drives placement (local input vs. relayed events) and whether a moving
 * block preview exists.
 */
function stepAndDrawFrame(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  v: TowerVisual,
  config: StackTowerConfig,
  deltaMs: number,
  nowMs: number,
  targetTopWorldY: number,
  smoothingSec: number,
  reducedMotion: boolean,
  movingBlock: { footprint: BlockFootprint; worldY: number } | null,
  climbHeight: number,
): void {
  const targetCameraOffset = cameraOffsetForTowerTop(targetTopWorldY, height);
  v.cameraOffset = reducedMotion ? targetCameraOffset : dampTowardTarget(v.cameraOffset, targetCameraOffset, deltaMs, smoothingSec);

  for (let i = v.fallingPieces.length - 1; i >= 0; i -= 1) {
    const piece = v.fallingPieces[i];
    stepFallingPiece(piece, deltaMs);
    const screenTop = worldToScreenY(piece.cy, v.cameraOffset, height) - piece.halfHeight;
    if (screenTop > height + PIECE_CULL_MARGIN_PX) v.fallingPieces.splice(i, 1);
  }

  let shakeX = 0;
  let shakeY = 0;
  if (v.shakeMag > 0) {
    v.shakeElapsedMs += deltaMs;
    if (v.shakeElapsedMs >= SHAKE_DURATION_MS) {
      v.shakeMag = 0;
    } else {
      const amp = v.shakeMag * (1 - v.shakeElapsedMs / SHAKE_DURATION_MS);
      shakeX = (Math.random() * 2 - 1) * amp * 0.5;
      shakeY = (Math.random() * 2 - 1) * amp;
    }
  }

  const impactAgeMs = nowMs - v.impactStartedMs;
  drawStackTowerFrame(ctx, {
    width,
    height,
    trackWidth: config.trackWidth,
    blockHeight: config.blockHeight,
    climbHeight,
    cameraOffset: v.cameraOffset,
    shakeX,
    shakeY,
    settledBlocks: v.settledBlocks,
    movingBlock,
    fallingPieces: v.fallingPieces,
    impact: Number.isFinite(v.impactStartedMs)
      ? { worldY: v.impactWorldY, ageMs: impactAgeMs, perfect: v.impactPerfect, streak: v.impactStreak, nearMiss: v.impactNearMiss }
      : null,
  });
}

function initialFootprintFor(config: StackTowerConfig): BlockFootprint {
  return createInitialTowerState(config, 'unused', 0).topFootprint;
}

// --- own (interactive, local-input) pane ------------------------------------

export interface OwnTowerEngineOptions {
  config?: StackTowerConfig;
  cameraSmoothingSec?: number;
  /**
   * 'timed' (C015, default): one shared window, a miss soft-resets, the run
   * ends at `matchEndsAtMs`. 'endless' (C017 Phase 3, spec §106-107): no
   * window — a miss ends *this* run outright (mirrors Solo Endless), the
   * tower stays as a frozen "monument" at whatever it reached.
   */
  mode?: 'timed' | 'endless';
  /** Wall-clock (`Date.now()`-based) moment the authoritative match window closes. Required for 'timed', ignored for 'endless' (no window to close). */
  matchEndsAtMs?: number;
  onScoreChanged?: (score: number) => void;
  onMultiplierChanged?: (multiplier: number) => void;
  onPerfectStreakChanged?: (streak: number) => void;
  onZoneChanged?: (zone: StackTowerZone) => void;
  /** Fired after every placement (hit or miss) — forward verbatim to the opponent over the socket. */
  onPlacement?: (event: StackTowerPlaceEvent) => void;
  /**
   * Fired exactly once. Timed mode: when the authoritative window closes.
   * Endless mode: the moment THIS player misses — the match itself may not
   * be over yet (the opponent could still be climbing), so unlike timed
   * mode this does not necessarily mean "tear this engine down": the caller
   * decides that once it also knows the match's real outcome.
   */
  onMatchEnd: (result: HeadToHeadPlayerResult) => void;
}

export interface OwnTowerEngine {
  destroy(): void;
}

export function mountOwnTowerEngine(container: HTMLElement, options: OwnTowerEngineOptions): OwnTowerEngine {
  const config = options.config ?? DEFAULT_STACK_TOWER_CONFIG;
  const smoothingSec = options.cameraSmoothingSec ?? 0.18;
  const mode = options.mode ?? 'timed';
  const reducedMotion = document.documentElement.dataset.reducedMotion === 'true';
  const initialFootprint = initialFootprintFor(config);

  const surface = mountCanvasSurface(container);
  const startedAtMs = performance.now();
  // Converts the server's wall-clock deadline into this engine's `performance.now()` timeline (they tick at the same rate; only the epoch differs). Endless mode has no window to convert — null throughout.
  const deadlinePerfMs = mode === 'timed' && options.matchEndsAtMs !== undefined ? startedAtMs + (options.matchEndsAtMs - Date.now()) : null;

  let state: TowerRunState = createInitialTowerState(config, crypto.randomUUID(), startedAtMs);
  const visual = createVisual(initialFootprint);
  let lastFrameMs = startedAtMs;
  let rafId: number | null = null;
  let destroyed = false;
  let finished = false;
  let lastZoneId = zoneForHeight(state.climbBlocks).id;
  let lastMultiplier = multiplierForClimb(state.climbBlocks, config);

  function place(): void {
    if (destroyed || finished) return;
    const nowMs = performance.now();
    const result = placeBlock(state, nowMs, config);
    if (!result.ok) return;
    const previousStreak = state.perfectStreak;
    const outcome = result.outcome;
    const scoreAtPlacement = state.score;
    const climbBlocksAtPlacement = state.climbBlocks;

    if (outcome.kind === 'hit') {
      state = result.state;
      // Height, not score — score can outpace height once the multiplier
      // kicks in (rules.ts), so it's no longer a safe stand-in for "how tall
      // is the tower" (both for drawing and for what gets relayed below).
      const worldY = state.climbBlocks * config.blockHeight;
      visual.settledBlocks.push({ footprint: state.topFootprint, worldY });
      const nearMiss = !outcome.perfect && footprintWidth(outcome.newFootprint) < config.startingBlockWidth * NEAR_MISS_WIDTH_FRACTION;
      if (outcome.overhang) {
        visual.fallingPieces.push(spawnOverhangPiece(outcome.overhang, worldY, config.blockHeight, MOVING_BLOCK_COLOR, nearMiss));
      }
      triggerImpact(visual, worldY, outcome.perfect, state.perfectStreak, nearMiss, nowMs, reducedMotion);
      triggerShake(visual, outcome.perfect ? PERFECT_SHAKE_UNITS : nearMiss ? NEAR_MISS_SHAKE_UNITS : HIT_SHAKE_UNITS, reducedMotion);
      // Own pane only — hearing both players' clicks at once would be
      // confusing about whose feedback is whose; the opponent pane stays
      // visual-only, matching how it's always worked.
      if (outcome.perfect) playStackTowerPerfect(state.perfectStreak);
      else if (nearMiss) playStackTowerNearMiss();
      else playStackTowerHit();
      options.onScoreChanged?.(state.score);
      const multiplier = multiplierForClimb(state.climbBlocks, config);
      if (multiplier !== lastMultiplier) {
        lastMultiplier = multiplier;
        options.onMultiplierChanged?.(multiplier);
      }
      if (state.perfectStreak !== previousStreak) options.onPerfectStreakChanged?.(state.perfectStreak);
      const zone = zoneForHeight(state.climbBlocks);
      if (zone.id !== lastZoneId) {
        lastZoneId = zone.id;
        options.onZoneChanged?.(zone);
      }
      options.onPlacement?.({
        kind: 'hit',
        score: state.score,
        height: state.climbBlocks,
        worldY,
        footprint: state.topFootprint,
        perfect: outcome.perfect,
        overhang: outcome.overhang,
        streak: state.perfectStreak,
        nearMiss,
      });
    } else {
      // The whole moving block topples either way. Direction mirrors Solo
      // Endless: which side of the (pre-reset) top footprint it overhung
      // when it missed.
      const worldY = (climbBlocksAtPlacement + 1) * config.blockHeight;
      const direction = outcome.movingBlock.min < state.topFootprint.min ? -1 : 1;
      visual.fallingPieces.push(spawnMissPiece(outcome.movingBlock, worldY, config.blockHeight, direction, MOVING_BLOCK_COLOR));
      triggerShake(visual, MISS_SHAKE_UNITS, reducedMotion);
      playStackTowerMiss();

      if (mode === 'endless') {
        // Spec §106-107: a miss ends THIS player's run outright — the tower
        // stays exactly as it fell, a frozen "monument". Unlike timed mode
        // there is no reset to speak of; `state.phase` is already
        // 'game_over' from `placeBlock` above, which is correct here (timed
        // mode is the one that has to undo it).
        options.onPlacement?.({ kind: 'miss', score: scoreAtPlacement, height: climbBlocksAtPlacement, worldY, movingBlock: outcome.movingBlock });
        finish();
        return;
      }

      // Timed miss (spec §103): the tower resets to its starting footprint
      // instead — score and cumulative stats carry through.
      resetVisualTower(visual, initialFootprint);
      state = resetTowerKeepingScore(result.state, config, nowMs);
      if (previousStreak !== 0) options.onPerfectStreakChanged?.(0);
      // The early-miss penalty (rules.ts) can lower score on a miss, not
      // just raise it on a hit — `state.score` (post-reset), not
      // `scoreAtPlacement`, is what's actually current now, both locally
      // and for what the opponent's live view of this player should show.
      if (state.score !== scoreAtPlacement) options.onScoreChanged?.(state.score);
      const multiplier = multiplierForClimb(state.climbBlocks, config);
      if (multiplier !== lastMultiplier) {
        lastMultiplier = multiplier;
        options.onMultiplierChanged?.(multiplier);
      }
      options.onPlacement?.({ kind: 'miss', score: state.score, height: 0, worldY, movingBlock: outcome.movingBlock });
    }
  }

  const unbindInput = bindStackTowerInput(surface.canvas, place);

  function finish(): void {
    if (destroyed || finished) return;
    finished = true;
    unbindInput();
    const result = deriveRunResult(state, config, startedAtMs, performance.now());
    options.onMatchEnd(result);
  }

  // The authoritative deadline is a `setTimeout`, not just a per-frame rAF
  // check: a backgrounded/hidden tab has its `requestAnimationFrame` callbacks
  // throttled or fully suspended by the browser (there's nothing to paint), so
  // a player who alt-tabs near the end of a match could otherwise never have
  // their real final sent — they'd fall back to the server's zeroed
  // last-resort default (see `fallbackFinal` in `stack-tower-match.ts`) even
  // though they played for real. `setTimeout` keeps firing (if a little late)
  // in a backgrounded tab, where rAF does not. The rAF-loop check below is
  // kept too, purely so a foregrounded tab finishes on the very next frame
  // rather than waiting on timer granularity; `finish()`'s guard makes calling
  // it from both harmless. Endless mode has no deadline to schedule at all —
  // `finish()` there is driven entirely by `place()` calling it on a miss.
  const finishTimeoutId =
    deadlinePerfMs !== null && options.matchEndsAtMs !== undefined
      ? window.setTimeout(finish, Math.max(0, options.matchEndsAtMs - Date.now()))
      : undefined;

  function frame(nowMs: number): void {
    if (destroyed) return;
    rafId = requestAnimationFrame(frame);
    const deltaMs = Math.min(nowMs - lastFrameMs, MAX_DELTA_MS);
    lastFrameMs = nowMs;

    if (!finished && deadlinePerfMs !== null && nowMs >= deadlinePerfMs) finish();

    const { width, height } = surface.size();
    const movingWorldY = (state.climbBlocks + 1) * config.blockHeight;
    const movingFootprint = finished ? null : currentMovingBlockFootprint(state, nowMs, config);
    stepAndDrawFrame(
      surface.ctx,
      width,
      height,
      visual,
      config,
      deltaMs,
      nowMs,
      movingWorldY,
      smoothingSec,
      reducedMotion,
      movingFootprint ? { footprint: movingFootprint, worldY: movingWorldY } : null,
      state.climbBlocks,
    );
  }
  rafId = requestAnimationFrame(frame);

  return {
    destroy() {
      destroyed = true;
      window.clearTimeout(finishTimeoutId);
      if (rafId !== null) cancelAnimationFrame(rafId);
      unbindInput();
      surface.destroy();
    },
  };
}

// --- opponent (reactive, network-driven) pane -------------------------------

export interface OpponentTowerEngineOptions {
  config?: StackTowerConfig;
  cameraSmoothingSec?: number;
  /** Same meaning as `OwnTowerEngineOptions.mode` — governs how a relayed miss event is drawn (soft-reset vs a frozen monument), not gameplay rules (the opponent pane never runs `placeBlock` itself). Defaults to 'timed'. */
  mode?: 'timed' | 'endless';
  onScoreChanged?: (score: number) => void;
}

export interface OpponentTowerEngine {
  applyEvent(event: StackTowerPlaceEvent): void;
  destroy(): void;
}

/**
 * Reconstructs the opponent's tower purely from relayed `place` events — no
 * local simulation of their moving block (we don't know their exact sweep
 * phase without a much heavier state sync), so this pane never draws a moving
 * block preview. The settled-block climb, falling debris and impact flash are
 * enough to read as "their tower, live" — spec's ask for this phase.
 */
export function mountOpponentTowerEngine(container: HTMLElement, options: OpponentTowerEngineOptions = {}): OpponentTowerEngine {
  const config = options.config ?? DEFAULT_STACK_TOWER_CONFIG;
  const smoothingSec = options.cameraSmoothingSec ?? 0.18;
  const mode = options.mode ?? 'timed';
  const reducedMotion = document.documentElement.dataset.reducedMotion === 'true';
  const initialFootprint = initialFootprintFor(config);

  const surface = mountCanvasSurface(container);
  const visual = createVisual(initialFootprint);
  let score = 0;
  // Tracked separately from `score`: once the multiplier kicks in, score can
  // climb faster than the tower does, so the camera/zone need the relayed
  // `event.height` directly rather than inferring it from score.
  let heightBlocks = 0;
  let lastFrameMs = performance.now();
  let rafId: number | null = null;
  let destroyed = false;

  function applyEvent(event: StackTowerPlaceEvent): void {
    if (destroyed) return;
    const nowMs = performance.now();
    if (event.kind === 'hit') {
      visual.settledBlocks.push({ footprint: event.footprint, worldY: event.worldY });
      if (event.overhang) {
        visual.fallingPieces.push(spawnOverhangPiece(event.overhang, event.worldY, config.blockHeight, MOVING_BLOCK_COLOR, event.nearMiss));
      }
      triggerImpact(visual, event.worldY, event.perfect, event.streak, event.nearMiss, nowMs, reducedMotion);
      score = event.score;
      heightBlocks = event.height;
      options.onScoreChanged?.(score);
    } else {
      const lastFootprint = visual.settledBlocks[visual.settledBlocks.length - 1]?.footprint ?? initialFootprint;
      const direction = event.movingBlock.min < lastFootprint.min ? -1 : 1;
      visual.fallingPieces.push(spawnMissPiece(event.movingBlock, event.worldY, config.blockHeight, direction, MOVING_BLOCK_COLOR));
      // Timed mode: the tower soft-resets, matching the local pane exactly.
      // Endless mode (spec §106-107): this miss ended the opponent's run —
      // their tower stays exactly as it stood, a frozen "monument", not a
      // reset. `event.height` is already the frozen height in this case
      // (the own pane sends its pre-miss climbBlocks, not 0), so the camera
      // target below stays correct without any extra branching there.
      if (mode !== 'endless') resetVisualTower(visual, initialFootprint);
      score = event.score;
      heightBlocks = event.height;
    }
  }

  function frame(nowMs: number): void {
    if (destroyed) return;
    rafId = requestAnimationFrame(frame);
    const deltaMs = Math.min(nowMs - lastFrameMs, MAX_DELTA_MS);
    lastFrameMs = nowMs;

    const { width, height } = surface.size();
    const targetTopWorldY = (heightBlocks + 1) * config.blockHeight;
    stepAndDrawFrame(surface.ctx, width, height, visual, config, deltaMs, nowMs, targetTopWorldY, smoothingSec, reducedMotion, null, heightBlocks);
  }
  rafId = requestAnimationFrame(frame);

  return {
    applyEvent,
    destroy() {
      destroyed = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      surface.destroy();
    },
  };
}
